import React, { useState, useEffect, useRef } from "react";
import { track } from "@vercel/analytics";
import domtoimage from "dom-to-image-more";
import { saveAs } from "file-saver";
import seedrandom from "seedrandom";
import { marked } from "marked";
import "./ResumeReview.css";

// Import all memes
import resumeMeme from "../memes/resume-meme.gif";
import yodaMeme from "../memes/yoda-meme.gif";
import raccoonMeme from "../memes/raccoon-sus-meme.gif";
import slayMeme from "../memes/slay-meme.gif";
import grassMeme from "../memes/grass-meme.gif";
import lyingMeme from "../memes/lying-meme.gif";

// Create an array of all memes for easier reference
const MEMES = [
  resumeMeme,
  yodaMeme,
  raccoonMeme,
  slayMeme,
  grassMeme,
  lyingMeme,
];

// Configure marked options globally
marked.setOptions({
  breaks: true, // Interpret line breaks as <br>
  gfm: true, // Use GitHub Flavored Markdown
  headerIds: false, // Don't add IDs to headers
  mangle: false, // Don't mangle email addresses
});

const ResumeReview = ({ reviewData, onReset }) => {
  const [activeTab, setActiveTab] = useState("roast");
  const [randomMeme, setRandomMeme] = useState(null);
  const [isGeneratingImage, setIsGeneratingImage] = useState(false);
  const [shareableImage, setShareableImage] = useState(null);
  const [shareableImageUrl, setShareableImageUrl] = useState(null);
  const resultRef = useRef(null);
  const shareContentRef = useRef(null);

  // Parse reviewData to ensure proper formatting
  const processedReviewData = useRef({});

  useEffect(() => {
    // Process the review data to ensure proper formatting
    try {
      // Try to parse the roast as JSON if it's a string that might be JSON
      if (typeof reviewData.roast === "string") {
        try {
          // Check if the roast is already in JSON format
          if (
            reviewData.roast.trim().startsWith("{") &&
            reviewData.roast.trim().endsWith("}")
          ) {
            const parsedRoast = JSON.parse(reviewData.roast);

            // If it's JSON with a content property, use that
            if (parsedRoast.content) {
              processedReviewData.current = {
                ...reviewData,
                roast: parsedRoast.content,
                isDefaultResponse: reviewData.isDefaultResponse || false,
              };
              return;
            }
          }
        } catch (jsonError) {
          // Not valid JSON, continue with normal processing
          console.log("Roast is not in JSON format, processing as plain text");
        }

        // For plain text, convert to a markdown-friendly format
        // Replace sequences of newlines with markdown paragraph breaks
        // Also handle literal \n in the text (which happens on mobile)
        const processedRoast = reviewData.roast
          .replace(/\\n\\n/g, "\n\n") // Replace literal \n\n with actual newlines
          .replace(/\\n/g, "\n") // Replace literal \n with actual newlines
          .replace(/\n{2,}/g, "\n\n") // Normalize multiple newlines to exactly two
          .replace(/\n(?!\n)/g, "  \n"); // Add two spaces before single newlines for markdown line breaks

        processedReviewData.current = {
          ...reviewData,
          roast: processedRoast,
          isDefaultResponse: reviewData.isDefaultResponse || false,
        };
      } else {
        processedReviewData.current = reviewData;
      }
    } catch (error) {
      console.error("Error processing review data:", error);
      processedReviewData.current = reviewData;
    }
  }, [reviewData]);

  const {
    roast,
    feedback,
    isDefaultResponse = false,
  } = processedReviewData.current;

  // Clean up object URL when component unmounts or when image changes
  useEffect(() => {
    return () => {
      if (shareableImageUrl) {
        URL.revokeObjectURL(shareableImageUrl);
      }
    };
  }, [shareableImageUrl]);

  // Generate a random meme only once when the component mounts
  useEffect(() => {
    track("resume_review_viewed");

    // Create a seed based on current timestamp and a random component
    const seed = `${Date.now()}-${Math.random()}`;
    const rng = seedrandom(seed);

    // Get a truly random index using the seeded random number generator
    const randomIndex = Math.floor(rng() * MEMES.length);
    console.log("Selected meme index:", randomIndex); // For debugging

    setRandomMeme(MEMES[randomIndex]);
  }, []);

  // Track tab changes
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    track("tab_change", { tab });
    // Reset shareable image when changing tabs
    if (shareableImageUrl) {
      URL.revokeObjectURL(shareableImageUrl);
      setShareableImageUrl(null);
    }
    setShareableImage(null);
  };

  // Function to convert text to HTML using marked
  const formatRoast = (text) => {
    if (!text) return "";

    try {
      // Process the text to enhance markdown formatting
      let enhancedText = text;

      // Ensure bold text uses markdown syntax
      enhancedText = enhancedText.replace(/\*\*(.*?)\*\*/g, "**$1**");

      // Configure marked options to minimize spacing
      marked.setOptions({
        breaks: true, // Interpret line breaks as <br>
        gfm: true, // Use GitHub Flavored Markdown
        headerIds: false, // Don't add IDs to headers
        mangle: false, // Don't mangle email addresses
      });

      // Convert the markdown to HTML
      const html = marked(enhancedText);

      // Further process the HTML to remove any unwanted spacing
      const processedHtml = html
        .replace(/<p>\s+/g, "<p>") // Remove spaces after paragraph opening
        .replace(/\s+<\/p>/g, "</p>") // Remove spaces before paragraph closing
        .replace(/<br>\s+/g, "<br>") // Remove spaces after line breaks
        .replace(/\s+<br>/g, "<br>"); // Remove spaces before line breaks

      return processedHtml;
    } catch (error) {
      console.error("Error formatting roast:", error);
      return text; // Return original text if there's an error
    }
  };

  // Track HireDash service link clicks
  const handleServiceClick = () => {
    track("service_link_click");
  };

  // Create a shareable content element
  const createShareableContent = () => {
    // Create container
    const container = document.createElement("div");
    container.style.width = "800px";
    container.style.padding = "30px";
    container.style.paddingTop = "60px"; // Extra padding at the top
    container.style.backgroundColor = "white";
    container.style.borderRadius = "12px";
    container.style.boxSizing = "border-box";
    container.style.fontFamily = "Arial, sans-serif";
    container.style.color = "#333";
    container.style.boxShadow = "0 2px 10px rgba(0,0,0,0.1)";
    container.style.display = "flex";
    container.style.flexDirection = "column";
    container.style.alignItems = "center";

    // Add logo
    const logoContainer = document.createElement("div");
    logoContainer.style.textAlign = "center";
    logoContainer.style.marginBottom = "10px";
    logoContainer.style.marginTop = "20px";
    logoContainer.style.width = "100%";
    logoContainer.style.padding = "0 20px";
    logoContainer.style.marginTop = "10px"; // Add margin at the top

    // Use the HireDash logo image
    const logo = document.createElement("img");
    logo.src = `${process.env.PUBLIC_URL}/hiredashlogo-long.png`; // Use PUBLIC_URL for correct path
    logo.style.height = "50px";
    logo.style.objectFit = "contain";
    logo.style.margin = "0 auto";
    logo.style.display = "block";

    // Fallback in case the logo fails to load
    logo.onerror = () => {
      const fallbackLogo = document.createElement("div");
      fallbackLogo.innerHTML = `<span style="color: #4169E1; font-size: 32px; font-weight: bold;">Hire</span><span style="color: #FFD700; font-size: 32px; font-weight: bold;">⚡</span>`;
      fallbackLogo.style.textAlign = "center";
      logoContainer.replaceChild(fallbackLogo, logo);
    };

    logoContainer.appendChild(logo);
    container.appendChild(logoContainer);

    // Add title
    const title = document.createElement("h2");
    title.textContent =
      activeTab === "roast"
        ? "Resume Roast Results 🔥"
        : "Resume Feedback Results 💯";
    title.style.textAlign = "center";
    title.style.color = "#00334e";
    title.style.fontSize = "24px";
    title.style.fontWeight = "bold";
    title.style.margin = "10px 0 25px 0";
    title.style.width = "100%";
    container.appendChild(title);

    // Add content based on active tab
    if (activeTab === "roast") {
      // Add meme if available
      if (randomMeme) {
        const memeContainer = document.createElement("div");
        memeContainer.style.marginBottom = "20px";
        memeContainer.style.width = "100%";
        memeContainer.style.display = "flex";
        memeContainer.style.justifyContent = "center";

        const memeImg = document.createElement("img");
        memeImg.src = randomMeme;
        memeImg.style.maxWidth = "300px";
        memeImg.style.maxHeight = "200px";
        memeImg.style.borderRadius = "8px";
        memeImg.style.objectFit = "contain";

        memeContainer.appendChild(memeImg);
        container.appendChild(memeContainer);
      }

      const contentDiv = document.createElement("div");
      contentDiv.style.background = "linear-gradient(135deg, #00334e, #145374)";
      contentDiv.style.color = "white";
      contentDiv.style.padding = "25px";
      contentDiv.style.borderRadius = "8px";
      contentDiv.style.marginBottom = "20px";
      contentDiv.style.width = "100%";
      contentDiv.style.boxSizing = "border-box";

      const contentTitle = document.createElement("h3");
      contentTitle.textContent = "The Brutal Truth 💀";
      contentTitle.style.marginBottom = "20px";
      contentTitle.style.fontSize = "32px";
      contentTitle.style.fontWeight = "1200";
      contentDiv.appendChild(contentTitle);

      // Create a wrapper for the content text with minimal styling
      const contentWrapper = document.createElement("div");
      contentWrapper.className = "shareable-content-wrapper";
      contentWrapper.style.fontSize = "12px";

      // Add the formatted content
      contentWrapper.innerHTML = formatRoast(roast);

      // Remove all margins and padding from paragraphs
      const paragraphs = contentWrapper.querySelectorAll("p");
      paragraphs.forEach((p, index) => {
        p.style.margin = "0";
        p.style.padding = "0";
        p.style.marginTop = "0";
        // Add 20px margin to all paragraphs except the last one
        p.style.marginBottom = index < paragraphs.length - 1 ? "20px" : "0";
      });

      contentDiv.appendChild(contentWrapper);
      container.appendChild(contentDiv);
    } else {
      // Feedback tab content - include all sections

      // Structure & Formatting section
      const structureDiv = createFeedbackSection(
        "Structure & Formatting 📝",
        feedback.structure,
        "#4a6fa5"
      );
      container.appendChild(structureDiv);

      // Content & Impact section
      const contentDiv = createFeedbackSection(
        "Content & Impact 💪",
        feedback.content,
        "#47a891"
      );
      container.appendChild(contentDiv);

      // Skills & Relevance section
      const skillsDiv = createFeedbackSection(
        "Skills & Relevance 🧠",
        feedback.skills,
        "#6b5ca5"
      );
      container.appendChild(skillsDiv);

      // Key Improvements section
      const improvementsDiv = document.createElement("div");
      improvementsDiv.style.borderLeft = "4px solid #ff6b6b";
      improvementsDiv.style.padding = "15px";
      improvementsDiv.style.marginBottom = "15px";
      improvementsDiv.style.backgroundColor = "#f9f9f9";
      improvementsDiv.style.borderRadius = "0 8px 8px 0";
      improvementsDiv.style.width = "100%";
      improvementsDiv.style.boxSizing = "border-box";

      const improvementsTitle = document.createElement("h3");
      improvementsTitle.textContent = "Key Improvements 🚀";
      improvementsTitle.style.color = "#00334e";
      improvementsTitle.style.fontSize = "16px";
      improvementsTitle.style.marginBottom = "10px";
      improvementsTitle.style.fontWeight = "bold";
      improvementsDiv.appendChild(improvementsTitle);

      const improvementsList = document.createElement("ul");
      improvementsList.style.margin = "0";
      improvementsList.style.paddingLeft = "20px";
      improvementsList.style.fontSize = "12px";

      // Include all improvements with no margins
      feedback.improvements.forEach((item, index) => {
        const li = document.createElement("li");
        li.textContent = item;
        li.style.margin = "0";
        li.style.padding = "0";
        // Add spacing between list items except the last one
        li.style.marginBottom =
          index < feedback.improvements.length - 1 ? "8px" : "0";
        improvementsList.appendChild(li);
      });

      improvementsDiv.appendChild(improvementsList);
      container.appendChild(improvementsDiv);
    }

    // Add URL
    const urlDiv = document.createElement("div");
    urlDiv.textContent = "resume-roaster.hire-dash.com";
    urlDiv.style.marginTop = "10px";
    urlDiv.style.marginBottom = "-50px";
    urlDiv.style.textAlign = "center";
    urlDiv.style.padding = "12px";
    urlDiv.style.backgroundColor = "#00334e";
    urlDiv.style.color = "white";
    urlDiv.style.borderRadius = "6px";
    urlDiv.style.fontSize = "16px";
    urlDiv.style.fontWeight = "bold";
    urlDiv.style.width = "100%";
    urlDiv.style.boxSizing = "border-box";
    container.appendChild(urlDiv);

    return container;
  };

  // Helper function to create feedback sections for shareable content
  const createFeedbackSection = (title, content, borderColor) => {
    const sectionDiv = document.createElement("div");
    sectionDiv.style.borderLeft = `4px solid ${borderColor}`;
    sectionDiv.style.padding = "15px";
    sectionDiv.style.marginBottom = "15px";
    sectionDiv.style.backgroundColor = "#f9f9f9";
    sectionDiv.style.borderRadius = "0 8px 8px 0";
    sectionDiv.style.width = "100%";
    sectionDiv.style.boxSizing = "border-box";
    sectionDiv.style.height = "260px";

    const sectionTitle = document.createElement("h3");
    sectionTitle.textContent = title;
    sectionTitle.style.color = "#00334e";
    sectionTitle.style.fontSize = "16px";
    sectionTitle.style.marginBottom = "10px";
    sectionTitle.style.fontWeight = "bold";
    sectionDiv.appendChild(sectionTitle);

    const sectionContent = document.createElement("p");
    sectionContent.textContent = content;
    sectionContent.style.margin = "0";
    sectionContent.style.padding = "0";
    sectionContent.style.fontSize = "18px";
    sectionContent.style.lineHeight = "1.2";
    sectionDiv.appendChild(sectionContent);

    return sectionDiv;
  };

  // Generate shareable image
  const generateShareableImage = async () => {
    setIsGeneratingImage(true);
    track("generate_shareable_image");

    try {
      // Create a shareable content element
      const container = createShareableContent();

      // Append to document temporarily
      container.style.position = "absolute";
      container.style.left = "-9999px";
      document.body.appendChild(container);

      // Remove only unnecessary margins and padding, keep bottom margins
      const allParagraphs = container.querySelectorAll("p");
      allParagraphs.forEach((p, index) => {
        // Reset all margins except bottom margin
        p.style.marginLeft = "0";
        p.style.marginRight = "0";
        p.style.marginTop = "0";
        p.style.marginBottom = "20px";
        // Bottom margin is already set in createShareableContent

        p.style.padding = "0";
      });

      const allListItems = container.querySelectorAll("li");
      allListItems.forEach((li, index) => {
        // Reset all margins except bottom margin
        li.style.marginLeft = "0";
        li.style.marginRight = "0";
        li.style.marginTop = "0";
        // Bottom margin is already set in createShareableContent

        li.style.padding = "0";
      });

      // Wait for images to load
      await new Promise((resolve) => setTimeout(resolve, 1000));

      // Generate image with dom-to-image
      const dataUrl = await domtoimage.toJpeg(container, {
        quality: 0.98,
        width: container.offsetWidth,
        height: container.offsetHeight,
        style: {
          margin: 0,
          padding: 0,
        },
        scale: 2,
        imagePlaceholder: `${process.env.PUBLIC_URL}/hiredashlogo-long.png`,
      });

      // Remove temporary element
      document.body.removeChild(container);

      // Convert data URL to Blob
      const response = await fetch(dataUrl);
      const blob = await response.blob();

      // Create object URL from blob
      if (shareableImageUrl) {
        URL.revokeObjectURL(shareableImageUrl);
      }
      const objectUrl = URL.createObjectURL(blob);

      setShareableImage(blob);
      setShareableImageUrl(objectUrl);
    } catch (error) {
      console.error("Error generating image:", error);
      alert("Failed to generate shareable image. Please try again.");
    } finally {
      setIsGeneratingImage(false);
    }
  };

  // Handle image download
  const handleDownload = () => {
    if (!shareableImage) return;

    track("download_shareable_image");
    saveAs(shareableImage, "my-resume-roast.jpg");
  };

  // Check if Web Share API is available
  const canUseWebShare = () => {
    return navigator.share && navigator.canShare;
  };

  // Handle native sharing (for mobile devices)
  const handleNativeShare = async () => {
    if (!shareableImage) {
      alert("Please generate an image first");
      return;
    }

    try {
      track("native_share_attempt");

      // Create file from blob
      const file = new File([shareableImage], "resume-roast.jpg", {
        type: "image/jpeg",
        lastModified: Date.now(),
      });

      const shareText =
        "I got my resume roasted with HireDash at resume-roaster.hire-dash.com 🔥";

      const shareData = {
        title: "Resume Roaster",
        text: shareText,
        files: [file],
      };

      if (navigator.canShare && navigator.canShare(shareData)) {
        await navigator.share(shareData);
        track("native_share_success");
      } else {
        // Fallback if file sharing is not supported
        await navigator.share({
          title: "Resume Roaster",
          text: shareText + " Check it out!",
          url: "https://resume-roaster.hire-dash.com",
        });
        track("native_share_fallback");
      }
    } catch (error) {
      console.error("Error sharing:", error);
      alert(
        "Sharing failed. You can download the image and share it manually."
      );
      track("native_share_error");
    }
  };

  // Handle LinkedIn sharing specifically
  const handleLinkedInShare = () => {
    const shareText =
      "I got my resume roasted with HireDash at resume-roaster.hire-dash.com 🔥";
    const url = "https://resume-roaster.hire-dash.com";

    // LinkedIn sharing URL with text and URL parameters
    const linkedinUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
      url
    )}&summary=${encodeURIComponent(shareText)}`;

    // Open in new window
    window.open(linkedinUrl, "_blank", "width=600,height=600");
    track("linkedin_share");
  };

  return (
    <div className="resume-review-container">
      <h2>
        Resume Roast Results <span className="fire-emoji">🔥</span>
      </h2>

      {isDefaultResponse && (
        <div className="rate-limit-alert">
          <p>
            <strong>⚠️ Yikes! Rate Limit Hit</strong>
          </p>
          <p>
            We're showing a default response because you've hit the limit of 2
            uploads per hour. Your current resume hasn't been properly roasted.
          </p>
          <p>Click "Try Another Resume" below to come back later.</p>
        </div>
      )}

      <div className="tab-container">
        <button
          className={`tab-button ${activeTab === "roast" ? "active" : ""}`}
          onClick={() => handleTabChange("roast")}
        >
          The Roast 🔥
        </button>
        <button
          className={`tab-button ${activeTab === "feedback" ? "active" : ""}`}
          onClick={() => handleTabChange("feedback")}
        >
          Actual Help 💯
        </button>
      </div>

      <div className="tab-content">
        {activeTab === "roast" && (
          <div className="roast-section" ref={resultRef}>
            <div className="meme-container">
              {randomMeme && (
                <img
                  src={randomMeme}
                  alt="Random roasting meme"
                  className="roast-meme"
                />
              )}
            </div>

            <div className="roast-card">
              <div
                className="roast-text"
                dangerouslySetInnerHTML={{ __html: formatRoast(roast) }}
              />
              <div className="roast-emoji-container">
                <span className="roast-emoji">🔥</span>
                <span className="roast-emoji">💀</span>
                <span className="roast-emoji">😭</span>
              </div>
            </div>

            <div className="social-share">
              <div className="share-image-section">
                <h3 className="share-heading">Share with your friends! 🚀</h3>
                {!shareableImageUrl ? (
                  <button
                    className="generate-image-button"
                    onClick={generateShareableImage}
                    disabled={isGeneratingImage}
                  >
                    {isGeneratingImage
                      ? "Generating..."
                      : "Generate Shareable Image"}
                  </button>
                ) : (
                  <div className="shareable-image-container">
                    <img
                      src={shareableImageUrl}
                      alt="Shareable resume roast"
                      className="shareable-image-preview"
                    />
                    <div className="share-buttons-container">
                      <button
                        className="download-image-button"
                        onClick={handleDownload}
                      >
                        Download Image
                      </button>

                      <div className="share-options-container">
                        {canUseWebShare() && (
                          <button
                            className="native-share-button"
                            onClick={handleNativeShare}
                            title="Share to any app"
                          >
                            Share Directly 📱
                          </button>
                        )}

                        <button
                          className="linkedin-share-button"
                          onClick={handleLinkedInShare}
                          title="Share to LinkedIn"
                        >
                          LinkedIn 💼
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <p className="disclaimer">
              Don't take it personally! We're just keeping it 100 with you.
            </p>
          </div>
        )}

        {activeTab === "feedback" && (
          <div className="feedback-section" ref={resultRef}>
            <div className="feedback-category">
              <h3>
                Structure & Formatting{" "}
                <span className="feedback-emoji">📝</span>
              </h3>
              <p>{feedback.structure}</p>
            </div>

            <div className="feedback-category">
              <h3>
                Content & Impact <span className="feedback-emoji">💪</span>
              </h3>
              <p>{feedback.content}</p>
            </div>

            <div className="feedback-category">
              <h3>
                Skills & Relevance <span className="feedback-emoji">🧠</span>
              </h3>
              <p>{feedback.skills}</p>
            </div>

            <div className="feedback-category">
              <h3>
                Key Improvements <span className="feedback-emoji">🚀</span>
              </h3>
              <ul className="improvements-list">
                {feedback.improvements.map((item, index) => (
                  <li key={index}>
                    <span className="list-marker">✓</span> {item}
                  </li>
                ))}
              </ul>
            </div>

            <div className="social-share">
              <div className="share-image-section">
                <h3 className="share-heading">Share with your friends! 🚀</h3>
                {!shareableImageUrl ? (
                  <button
                    className="generate-image-button"
                    onClick={generateShareableImage}
                    disabled={isGeneratingImage}
                  >
                    {isGeneratingImage
                      ? "Generating..."
                      : "Generate Shareable Image"}
                  </button>
                ) : (
                  <div className="shareable-image-container">
                    <img
                      src={shareableImageUrl}
                      alt="Shareable resume feedback"
                      className="shareable-image-preview"
                    />
                    <div className="share-buttons-container">
                      <button
                        className="download-image-button"
                        onClick={handleDownload}
                      >
                        Download Image
                      </button>

                      <div className="share-options-container">
                        {canUseWebShare() && (
                          <button
                            className="native-share-button"
                            onClick={handleNativeShare}
                            title="Share to any app"
                          >
                            Share Directly 📱
                          </button>
                        )}

                        <button
                          className="linkedin-share-button"
                          onClick={handleLinkedInShare}
                          title="Share to LinkedIn"
                        >
                          LinkedIn 💼
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="hire-dash-promotion">
        <h3>Check out HireDash's Main Product</h3>
        <p>
          HireDash is an AI-powered pre-screening tool to streamline recruitment{" "}
          <br />
          and find the best candidates for a job. <br />
          We use cutting-edge AI so you can breeze through screening and trust
          us, <br /> you'll want to level up before your hiring game gets
          roasted by the competition.
        </p>
        <div className="social-links">
          <a
            href="https://www.linkedin.com/company/hiredashofficial"
            target="_blank"
            rel="noopener noreferrer"
            className="social-link linkedin"
            onClick={() => track("linkedin_click")}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
            >
              <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" />
            </svg>
          </a>
          <a
            href="https://www.facebook.com/share/1AaBi9QniZ/?mibextid=wwXIfr"
            target="_blank"
            rel="noopener noreferrer"
            className="social-link facebook"
            onClick={() => track("facebook_click")}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
            >
              <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-3 7h-1.924c-.615 0-1.076.252-1.076.889v1.111h3l-.238 3h-2.762v8h-3v-8h-2v-3h2v-1.923c0-2.022 1.064-3.077 3.461-3.077h2.539v3z" />
            </svg>
          </a>
          <a
            href="https://www.instagram.com/hiredash_"
            target="_blank"
            rel="noopener noreferrer"
            className="social-link instagram"
            onClick={() => track("instagram_click")}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
            >
              <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
            </svg>
          </a>
        </div>
        <a
          href="https://landing.hire-dash.com"
          target="_blank"
          rel="noopener noreferrer"
          className="hire-dash-button"
          onClick={handleServiceClick}
        >
          Check Out HireDash <span className="button-emoji">✨</span>
        </a>
      </div>

      <button
        className="reset-button"
        onClick={() => {
          track("upload_another_resume");
          onReset();
        }}
      >
        Try Another Resume
      </button>
    </div>
  );
};

export default ResumeReview;
